import { Injectable } from '@angular/core'
import { Store } from '@ngxs/store'
import { combineLatest, distinctUntilChanged, Subject } from 'rxjs'
import { ScreenSizeService } from '~app/services/screen-size/screen-size.service'
import { WindowRefService } from '~app/services/window/window-ref.service'
import { PlaybackState } from '~store/playback/playback.state'

// fallback --spacer variable
// MUST BE EXACTLY THE SAME AS 1REM
export const VERTICAL_SPACER_PX = 16
const LINE_HEIGHT = 35

@Injectable({
  providedIn: 'root',
})
export class HeaderSizeService {
  private readonly spacer: number
  /**
   * Used to identify when the header is present on the Court Recording Page
   */
  readonly headerIsRendered = new Subject<boolean>()

  private headerSize: number | undefined

  constructor(
    private readonly windowRef: WindowRefService,
    private readonly store: Store,
    private readonly screenSize: ScreenSizeService,
  ) {
    this.spacer = VERTICAL_SPACER_PX
    combineLatest([this.store.select(PlaybackState.isPlaybackPage), this.screenSize.size, this.headerIsRendered])
      .pipe(distinctUntilChanged())
      .subscribe(([isPlaybackPage]) => {
        this.headerSize = this.windowRef.getElementHeight('header')
        /**
         * On the recording playback page, add the height of the waveform and player bar.
         * This code is reached early when loading the page and the player has not had
         * time to render yet. The top player's height is constant so rather than get the
         * height via DOM, it is hardcoded.
         */
        if (this.headerSize && isPlaybackPage) {
          this.headerSize += 180
        }
      })
  }

  // Add the +1 as we do not want to render elements whose bottom is exactly at the offset point.
  isElementAtLeastPartiallyInViewportUnderHeader(el: HTMLElement): boolean {
    const currentHeaderSizeWithOffsetPx = this.getCurrentPlaybackHeaderSizePx()
    const offset = currentHeaderSizeWithOffsetPx ? currentHeaderSizeWithOffsetPx : VERTICAL_SPACER_PX
    return this.windowRef.isElementInViewport(el, offset + 1)
  }

  // Full Header since including all playback controls
  //
  // Performance: This is not as slow as i thought it would be.
  // We could cache this value
  getCurrentPlaybackHeaderSizePx(): number | undefined {
    return this.headerSize
  }

  // Full Header since including all playback controls
  getCurrentHeaderSizeWithOffsetPx(): number | undefined {
    const currentPlaybackHeaderSizePx = this.getCurrentPlaybackHeaderSizePx()
    if (currentPlaybackHeaderSizePx === undefined) {
      return undefined
    }
    const bottomPadding = this.spacer * 1.5
    const heightOfThreeLines = LINE_HEIGHT * 3
    return currentPlaybackHeaderSizePx + bottomPadding + heightOfThreeLines
  }
}
